import { PaginationControls } from "@/components/pagination-controls";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { usePagination } from "@/hooks/use-pagination";
import { AuditLogService } from "@/lib/api/audit-log.service";
import { useAuth } from "@/lib/auth";
import type { AuditLogEntry } from "@/lib/schema";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  Layers,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

const actionIcons: Record<string, React.ElementType> = {
  content_created: FileText,
  content_updated: FileText,
  content_submitted: Clock,
  content_approved: CheckCircle2,
  content_published: CheckCircle2,
  content_rejected: AlertCircle,
  content_archived: Activity,
  content_changes_requested: AlertTriangle,
  content_version_created: Layers,
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function ActivityPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [actionFilter, setActionFilter] = useState<string>("all");

  const isCoach = user?.roles.includes("coach");

  const logFilters = {
    limit: 100,
    entityType: "content",
    ...(isCoach && user?.id ? { actorId: user.id } : {}),
    ...(actionFilter !== "all" ? { actionType: actionFilter } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["/api/audit-logs", logFilters],
    queryFn: () => AuditLogService.findAll(logFilters),
  });

  const filteredLogs: AuditLogEntry[] = data?.entries ?? [];
  const { pageItems, ...paginationProps } = usePagination(filteredLogs);

  return (
    <div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      data-testid="activity-page"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/content-operations")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Content Activity
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCoach
              ? "Your content activity timeline"
              : "All content activity across the platform"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger
            className="w-[180px]"
            data-testid="select-action-filter"
          >
            <SelectValue placeholder="Action Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="content_created">Created</SelectItem>
            <SelectItem value="content_updated">Updated</SelectItem>
            <SelectItem value="content_submitted">Submitted</SelectItem>
            <SelectItem value="content_approved">Approved</SelectItem>
            <SelectItem value="content_published">Published</SelectItem>
            <SelectItem value="content_withdrawn">Withdrawn</SelectItem>
            <SelectItem value="content_archived">Archived</SelectItem>
            <SelectItem value="content_restored">Restored</SelectItem>
            <SelectItem value="content_changes_requested">
              Changes Requested
            </SelectItem>
            <SelectItem value="content_version_created">
              Version Created
            </SelectItem>
          </SelectContent>
        </Select>
        {filteredLogs.length > 0 && (
          <span
            className="text-sm text-muted-foreground ml-auto tabular-nums"
            data-testid="text-activity-count"
          >
            {filteredLogs.length} events
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : filteredLogs.length === 0 ? (
        <Card>
          <CardContent
            className="p-6 text-center text-muted-foreground"
            data-testid="text-no-activity"
          >
            No recent activity to display.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {pageItems.map((log) => {
            const ActionIcon = actionIcons[log.actionType] || Activity;
            return (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 rounded-md hover-elevate"
                data-testid={`activity-${log.id}`}
              >
                <div className="mt-0.5 shrink-0">
                  <ActionIcon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm"
                    data-testid={`text-activity-desc-${log.id}`}
                  >
                    {log.description}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span
                      className="text-xs text-muted-foreground"
                      data-testid={`text-activity-actor-${log.id}`}
                    >
                      {log.actorUsername || "System"}
                    </span>
                    <span
                      className="text-xs text-muted-foreground"
                      data-testid={`text-activity-time-${log.id}`}
                    >
                      {formatRelativeTime(log.timestamp)}
                    </span>
                    {log.actionType && (
                      <span className="text-xs text-muted-foreground opacity-60">
                        {log.actionType.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        </div>
      )}
    </div>
  );
}
